import debounce from 'lodash/debounce'
import find from 'lodash/find'
import first from 'lodash/first'
import map from 'lodash/map'
import React, {
  FC,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import styled from 'styled-components'

import CloseIcon from '@Assets/SVG/Close'
import PhoneIcon from '@Assets/SVG/Phone'
import { SVGIcon } from '@Components/Icons'
import { Link } from '@Components/Link'
import { SidePane } from '@Components/SidePane'
import { TrustIcons } from '@Components/TrustIcons'
import { NavigationEvent } from '@Events/Navigation'
import { MetadataContext } from '@Globals/Metadata'
import { flexCenterChildren, flexCenterChildrenColumn } from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'
import { useCustomerSupportData } from '@Globals/useCustomerSupportData'

import { CategoryItem } from './CategoryItem'
import { CMSPosts } from './CMSPosts'
import { CustomerSupportMenu } from './CustomerSupportMenu'
import { LanguageToggle } from './LanguageToggle'
import { SubCategoryMenu } from './SubCategoryMenu'

interface MobileMenuProps {
  closeMobileMenu: () => void
  isMenuOpen: boolean
  isSubMenuOpen: boolean
  onCloseSubCategory: () => void
  onTopCategoryClick: (id: string) => void
  selectedCategoryID?: string
  useDelayOnClose: boolean
}

const StyledCloseIcon = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  font-size: 2rem;
  padding: 16px 17px 15px 25px;
  line-height: 1;
  cursor: pointer;
`

const StyledHeader = styled.div`
  ${flexCenterChildrenColumn};
  justify-content: center;
  height: 70px;
  background-color: ${({ theme }) => theme.colors.greyLightest};
  border-bottom: 1px solid ${({ theme }) => theme.colors.greyLighter};
`

const StyledLogo = styled(Link)`
  display: block;
  width: 150px;
`

const StyledOutletItem = styled.div`
  border-top: 7px solid ${({ theme }) => theme.colors.greyLighter};
`

const StyledCallUs = styled.div`
  background-color: #fff;
  padding: 30px 0;
  border-bottom: 1px solid #ddd;
  ${flexCenterChildrenColumn}
`

const StyledPhoneWrap = styled.a`
  ${flexCenterChildren}
`

const StyledPhoneIcon = styled.div`
  margin-right: 10px;
  margin-left: -10px;
`

const StyledPhoneNumber = styled.span`
  font-size: 1.75rem;
  color: ${({ theme }) => theme.colors.primary};
`

const StyledOpenHours = styled.span`
  color: ${({ theme }) => theme.colors.grey};
`

const StyledTrustIcons = styled.div`
  padding: 60px 0 100px;
`

export const MobileMenu: FC<MobileMenuProps> = (
  {
    closeMobileMenu,
    isMenuOpen,
    isSubMenuOpen,
    onCloseSubCategory,
    onTopCategoryClick,
    selectedCategoryID,
    useDelayOnClose,
  }: MobileMenuProps,
) => {
  const { t } = useTranslation()
  const {
    categories,
    categoriesMenu,
    store,
    storeURL,
  } = useContext(MetadataContext)
  const outletCategory = useMemo(() => find(categories, ['id', '224']), [categories])
  const selectedCategory = useMemo(() => {
    const item = find(
      categoriesMenu,
      ['id', selectedCategoryID],
    )

    return item ?? first(categoriesMenu)
  }, [selectedCategoryID, categoriesMenu])
  const customerSupportData = useCustomerSupportData()
  const phone = store?.config?.phone

  const onCloseDelay = useDelayOnClose ? 250 : 150
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onCloseMobileMenu = useCallback(
    debounce(() => closeMobileMenu(), onCloseDelay),
    [onCloseDelay, closeMobileMenu],
  )

  return (
    <>
      {/* Mobile menu */}
      <SidePane isShowing={isMenuOpen} onClose={onCloseMobileMenu}>
        {/* Close icon */}
        <StyledCloseIcon onClick={onCloseMobileMenu}>
          <SVGIcon icon={CloseIcon} size="12px" />
        </StyledCloseIcon>

        {/* Header */}
        <StyledHeader>
          <StyledLogo
            event={new NavigationEvent({
              attributes: {
                dest_page_type: 'frontpages',
                dest_page_type_id: '',
                source_id: 'mobile_menu',
              },
              eventName: 'nav_link_click',
            })}
            href={storeURL('/')}
          >
            <img
              alt="Trendhim"
              src="https://res.cloudinary.com/trendhim/image/upload/v1570536495/frontend/trendhim-logo-with-text.svg?v3"
            />
          </StyledLogo>
        </StyledHeader>

        {/* Body */}
        <div>
          <LanguageToggle />

          {/* Categories */}
          <div>
            {map(categoriesMenu, ({ id, name }) => (
              <CategoryItem
                key={id}
                id={id}
                onTopCategoryClick={() => onTopCategoryClick(id)}
                title={name || ''}
              />
            ))}
          </div>

          {/* Outlet link */}
          {outletCategory && (
            <StyledOutletItem>
              <Link
                event={new NavigationEvent({
                  attributes: {
                    dest_page_type: 'category',
                    dest_page_type_id: outletCategory.id,
                    source_id: 'mobile_menu',
                  },
                  eventName: 'nav_link_click',
                })}
                href={outletCategory.url || ''}
              >
                <CategoryItem
                  id={outletCategory.id}
                  title={outletCategory.name || ''}
                />
              </Link>
            </StyledOutletItem>
          )}

          {/* CMS posts */}
          <CMSPosts />

          {/* Call us */}
          <StyledCallUs>
            <StyledPhoneWrap href={`tel:${phone}`}>
              <StyledPhoneIcon>
                <SVGIcon color="primary" icon={PhoneIcon} size="24px" />
              </StyledPhoneIcon>
              <StyledPhoneNumber>{phone}</StyledPhoneNumber>
            </StyledPhoneWrap>
            <StyledOpenHours>{t('header:PHONE_OPEN_HOURS')}</StyledOpenHours>
          </StyledCallUs>

          {/* Customer support links */}
          <CustomerSupportMenu customerSupportData={customerSupportData} />
          <StyledTrustIcons>
            <TrustIcons variant="mobile-menu" />
          </StyledTrustIcons>
        </div>
      </SidePane>

      {selectedCategory && (
        <SubCategoryMenu
          category={selectedCategory}
          isShowing={isSubMenuOpen}
          onCloseClick={onCloseSubCategory}
        />
      )}
    </>
  )
}
